import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaBell, FaUser, FaSignOutAlt, FaHeart, FaComments, FaChevronDown } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const notificationCount = 3;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setDropdownOpen(false);
  };

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/browse', label: 'Browse Profiles' },
    { to: '/matches', label: 'Matches' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/about', label: 'About' },
  ];

  const isActive = (path) => location.pathname === path;

  const getInitials = () => {
    if (!user?.name) return 'U';
    return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <nav className="sticky top-0 z-50 bg-[#8B0000] shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-1 flex-shrink-0">
            <div className="flex flex-col leading-tight">
              <span className="text-[#FFD700] text-xl font-bold font-['Noto_Sans_Devanagari']">
                विवाह आरंभ
              </span>
              <span className="text-[#FFF8F0] text-[10px] tracking-wider font-['Playfair_Display'] -mt-1">
                Vivah Arambh
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive(link.to)
                    ? 'bg-[#DC143C] text-white'
                    : 'text-[#FFF8F0] hover:bg-[#a50000] hover:text-[#FFD700]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                {/* Notification Bell */}
                <Link to="/notifications" className="relative text-[#FFF8F0] hover:text-[#FFD700] transition-colors">
                  <FaBell className="text-xl" />
                  {notificationCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#DC143C] text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center border-2 border-[#8B0000]">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                </Link>

                {/* User Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center space-x-2 text-[#FFF8F0] hover:text-[#FFD700] transition-colors focus:outline-none"
                  >
                    {user?.profilePhoto ? (
                      <img
                        src={user.profilePhoto}
                        alt={user.name}
                        className="h-8 w-8 rounded-full object-cover border-2 border-[#FFD700]"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#DC143C] to-[#B8860B] flex items-center justify-center text-white text-sm font-bold border-2 border-[#FFD700]">
                        {getInitials()}
                      </div>
                    )}
                    <span className="text-sm font-medium max-w-[100px] truncate">
                      {user?.name?.split(' ')[0] || 'User'}
                    </span>
                    <FaChevronDown className={`text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50 animate-fadeIn">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-[#8B0000]">{user?.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      </div>
                      <Link
                        to="/profile"
                        className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-[#FFF8F0] hover:text-[#8B0000] transition-colors"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <FaUser className="text-[#B8860B]" />
                        <span>My Profile</span>
                      </Link>
                      <Link
                        to="/dashboard"
                        className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-[#FFF8F0] hover:text-[#8B0000] transition-colors"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <FaUser className="text-[#B8860B]" />
                        <span>Dashboard</span>
                      </Link>
                      <Link
                        to="/matches"
                        className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-[#FFF8F0] hover:text-[#8B0000] transition-colors"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <FaHeart className="text-[#DC143C]" />
                        <span>Matches</span>
                      </Link>
                      <Link
                        to="/chat"
                        className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-[#FFF8F0] hover:text-[#8B0000] transition-colors"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <FaComments className="text-[#B8860B]" />
                        <span>Chat</span>
                      </Link>
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          onClick={handleLogout}
                          className="flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full text-left transition-colors"
                        >
                          <FaSignOutAlt />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className="text-[#FFF8F0] hover:text-[#FFD700] text-sm font-medium transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="bg-[#FFD700] text-[#8B0000] px-4 py-2 rounded-full text-sm font-bold hover:bg-[#B8860B] hover:text-white transition-colors shadow-md"
                >
                  Register Free
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-3">
            {isAuthenticated && (
              <Link to="/notifications" className="relative text-[#FFF8F0] hover:text-[#FFD700]">
                <FaBell className="text-lg" />
                {notificationCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#DC143C] text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center border border-[#8B0000]">
                    {notificationCount}
                  </span>
                )}
              </Link>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-[#FFF8F0] hover:text-[#FFD700] focus:outline-none transition-colors"
            >
              {mobileMenuOpen ? <FaTimes className="text-2xl" /> : <FaBars className="text-2xl" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden ${
          mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-[#6d0000] px-4 pt-2 pb-4 space-y-1 border-t border-[#a50000]">
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={`block px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive(link.to)
                  ? 'bg-[#DC143C] text-white'
                  : 'text-[#FFF8F0] hover:bg-[#8B0000] hover:text-[#FFD700]'
              }`}
            >
              {link.label}
            </Link>
          ))}

          {isAuthenticated ? (
            <>
              <div className="border-t border-[#a50000] my-2 pt-2">
                <div className="flex items-center space-x-3 px-3 py-2">
                  {user?.profilePhoto ? (
                    <img src={user.profilePhoto} alt={user.name} className="h-8 w-8 rounded-full object-cover border-2 border-[#FFD700]" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#DC143C] to-[#B8860B] flex items-center justify-center text-white text-sm font-bold border-2 border-[#FFD700]">
                      {getInitials()}
                    </div>
                  )}
                  <span className="text-[#FFD700] text-sm font-medium">{user?.name}</span>
                </div>
                <Link to="/profile" className="flex items-center space-x-3 px-3 py-2.5 text-[#FFF8F0] hover:text-[#FFD700] text-sm">
                  <FaUser /> <span>My Profile</span>
                </Link>
                <Link to="/dashboard" className="flex items-center space-x-3 px-3 py-2.5 text-[#FFF8F0] hover:text-[#FFD700] text-sm">
                  <FaUser /> <span>Dashboard</span>
                </Link>
                <Link to="/matches" className="flex items-center space-x-3 px-3 py-2.5 text-[#FFF8F0] hover:text-[#FFD700] text-sm">
                  <FaHeart /> <span>Matches</span>
                </Link>
                <Link to="/chat" className="flex items-center space-x-3 px-3 py-2.5 text-[#FFF8F0] hover:text-[#FFD700] text-sm">
                  <FaComments /> <span>Chat</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-3 px-3 py-2.5 text-red-300 hover:text-red-100 text-sm w-full text-left"
                >
                  <FaSignOutAlt /> <span>Logout</span>
                </button>
              </div>
            </>
          ) : (
            <div className="border-t border-[#a50000] my-2 pt-3 flex flex-col space-y-2">
              <Link
                to="/login"
                className="text-center text-[#FFF8F0] border border-[#FFD700] px-4 py-2 rounded-full text-sm font-medium hover:bg-[#FFD700] hover:text-[#8B0000] transition-colors"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="text-center bg-[#FFD700] text-[#8B0000] px-4 py-2 rounded-full text-sm font-bold hover:bg-[#B8860B] hover:text-white transition-colors"
              >
                Register Free
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
